/**
 * Plan & billing, the subscribe screen, the plan comparison table and the
 * invoice history. Plan names, descriptions and feature labels come from the
 * server and are never keys here. Conventions: see `common.ts`.
 */
const billing: Record<string, string> = {
  upgrade_to: 'Upgrade to {plan}',
  collapse_all: 'Collapse all',
  expand_all: 'Expand all',
  cancelling: 'Cancelling…',

  // Reading a feature value
  'limit.unlimited': 'Unlimited',
  'value.included': 'Included',
  'value.not_included': 'Not included',
  'value.addon': 'Add-on',

  // Billing cycles: "₹999/mo", "/ month", "Every 3 months"
  'interval.short.MONTHLY': 'mo',
  'interval.short.QUARTERLY': 'qtr',
  'interval.short.YEARLY': 'yr',
  'interval.long.MONTHLY': 'month',
  'interval.long.QUARTERLY': 'quarter',
  'interval.long.YEARLY': 'year',
  'cycle.months': '{months} months',
  'cycle.years': '{years} years',
  'cycle.adverb.monthly': 'monthly',
  'cycle.adverb.quarterly': 'quarterly',
  'cycle.adverb.yearly': 'yearly',
  'cycle.adverb.every': 'every {cycle}',
  'cycle_tabs.MONTHLY': 'Billed monthly',
  'cycle_tabs.QUARTERLY': 'Billed quarterly',
  'cycle_tabs.YEARLY': 'Billed yearly',

  // Plan comparison table
  'compare.feature': 'Feature',
  'compare.current': 'Current',
  'compare.per_month': '/mo',
  'compare.all_same': 'Every plan includes the same things.',
  'compare.display_only': 'Display only',
  'compare.display_only_hint': 'Shown to merchants, but nothing in the API refuses a request over it.',
  'compare.editor.label': '{feature} on {plan}',
  'compare.editor.label_in_unit': '{label}, in {unit}',
  'compare.group.features_one': '{count} feature',
  'compare.group.features_other': '{count} features',
  'compare.group.differ_one': '{count} differs',
  'compare.group.differ_other': '{count} differ',
  'compare.legend.billed_separately': 'billed separately',
  'compare.legend.limits': '{sample} = limit · {unlimited} = no limit',
  'compare.legend.display_only': '{badge} = shown, not gated by the API',

  // Invoice history
  'invoices.columns.date': 'Date',
  'invoices.columns.plan_period': 'Plan & period',
  'invoices.columns.amount': 'Amount',
  'invoices.columns.status': 'Status',
  'invoices.columns.reference': 'Reference',
  'invoices.kind.RECURRING': 'Auto-renewal',
  'invoices.kind.AUTHORIZATION': 'Auto-renewal set-up',
  'invoices.kind.SETUP_ADDON': 'Store setup',
  'invoices.bank_transfer': 'Bank transfer',
  'invoices.empty': 'No charges yet.',
  'invoices.status.PAID': 'Paid',
  'invoices.status.FAILED': 'Failed',
  'invoices.status.PENDING': 'Awaiting payment',
  'invoices.status.CANCELLED': 'Cancelled',
  'invoices.paid_on': 'Paid {date}',
  'invoices.period': 'Period',
  'invoices.charge_on': '{plan} on {date}',
  'invoices.due_on': 'Due {date}',
  'invoices.setup_paid_upfront': '{amount} store setup already paid',

  // Route guard for a feature the plan does not include
  'guard.title': '{feature} is not included in your plan',
  'guard.description': 'Anything you created here is kept safe. Upgrade your plan to start using it again.',
  'guard.see_plans': 'See plans',

  // Plan & billing page
  'page.title': 'Plan & billing',
  'page.description': 'Your plan, what it includes, how much of it you have used, and what upgrading adds.',
  'page.continue_setup': 'Continue setting up your store — {step}',

  'errors.change_failed': 'Could not change your plan.',
  'errors.load_failed': 'Could not load your plan. Refresh to try again.',

  'status.awaiting_authorisation': 'Awaiting authorisation',
  'status.free_trial': 'Free trial',
  'status.starts': 'Starts {date}',
  'status.active': 'Active',
  'status.payment_overdue': 'Payment overdue',
  'status.paid_features_paused': 'Paid features paused',
  'status.ends': 'Ends {date}',
  'status.trial': 'Trial',
  'status.period_ended': 'Period ended',
  'status.cancelled': 'Cancelled',
  'status.free_plan': 'Free plan',

  'notices.period_end_fallback': 'the end of your current period',
  'notices.changed':
    'You are now on {plan}. Razorpay adjusts the charge for the rest of this period under your mandate.',
  'notices.scheduled': 'Your plan changes on {date}. Until then you keep everything you have paid for.',
  'notices.scheduled_to': 'Your plan changes to {plan} on {date}.',
  'notices.cancel_scheduled': 'Auto-renewal is off. You keep your paid features until {date}.',
  'notices.cancel_scheduled_then_free':
    'Auto-renewal is off. You keep your paid features until {date}, then move to the Free plan.',
  'notices.now_on': 'You are now on {plan}.',
  'notices.updating': 'Your plan is being updated.',
  'notices.trial_started':
    'Your {days}-day free trial has started. Nothing is charged until {date}, when the first payment of {amount} is taken.',
  'notices.trial_of_plan_started':
    'Your {days}-day free trial of {plan} has started. Nothing is charged until {date}, when the first payment of {amount} is taken.',
  'notices.auto_renew_on': 'Auto-renewal is on. Your plan is active.',
  'notices.mandate_authorised':
    'Mandate authorised. Your plan activates as soon as Razorpay confirms it — usually within a minute.',
  'notices.confirm_failed':
    'We could not confirm the authorisation yet. Your plan activates once Razorpay confirms it — refresh in a minute.',
  'notices.authorisation_cancelled':
    'Authorisation cancelled. You are still on your current plan; the pending set-up can be cancelled below.',
  'notices.authorisation_not_completed':
    'Authorisation not completed. Nothing has changed and nothing was charged — continue whenever you are ready.',
  'notices.checkout_open_failed': 'We could not open the payment window. Try again in a moment.',
  'notices.payment_received':
    'Payment received. Your plan activates as soon as the bank confirms it — usually within a minute.',
  'notices.payment_received_new_plan':
    'Payment received. Your new plan activates as soon as the bank confirms it — usually within a minute.',
  'notices.payment_cancelled': 'Payment cancelled. Nothing has changed.',
  'notices.payment_cancelled_current_plan': 'Payment cancelled. You are still on your current plan.',
  'notices.trial_cancelled':
    'Your free trial is cancelled and Razorpay will not take the first payment. You keep your paid features until {date}.',
  'notices.setup_cancelled': 'Set-up cancelled. You are still on your current plan.',
  'notices.kept_plan': 'You stay on your current plan.',

  // Razorpay Checkout
  'checkout.first_period_then_auto_renewing': '{plan} plan — {amount} today, then auto-renewing',
  'checkout.plan_auto_renewing': '{plan} plan — auto-renewing',
  'checkout.plan_trial_then_auto_renewing': '{plan} plan — {days}-day free trial, then auto-renewing',
  'checkout.auto_renewing_plan': 'Auto-renewing plan',
  'checkout.order_number': 'Plan {code}',

  'past_due.title': 'Your last automatic payment did not go through',
  'past_due.reason': ' ({reason})',
  'past_due.retry': '. Razorpay will retry it.',
  'past_due.grace': 'If it is still unpaid on {date}, your paid features pause until a payment succeeds.',
  'past_due.paused': 'Your paid features are paused until a payment succeeds; nothing has been deleted.',

  'pending.title': 'Auto-renewal is waiting to be authorised.',
  'pending.description': 'Finish the authorisation in Razorpay Checkout, or start again from the plans below.',
  'pending.continue': 'Continue authorisation',
  'pending.refresh': 'Refresh status',
  'pending.cancel_setup': 'Cancel set-up',

  'scheduled.move': 'You move to {plan} on {date}, when your current period ends.',
  'scheduled.keep': 'Keep {plan}',
  'scheduled.keep_fallback': 'current plan',

  'current.title': 'Current plan',
  'current.trial_badge': '{days}-day free trial',
  'current.loading': 'Loading your plan…',
  'current.razorpay_subscription': 'Razorpay subscription',
  'current.overrides_one':
    'Your account has bespoke limits agreed with NeonCarts on {count} feature. They are reflected in the figures on this page.',
  'current.overrides_other':
    'Your account has bespoke limits agreed with NeonCarts on {count} features. They are reflected in the figures on this page.',
  'current.change_plan': 'Change plan',
  'current.compare_upgrade': 'Compare & upgrade',
  'current.switch_auto_renew': 'Switch to auto-renew',
  'current.bigger_plans_one': '{count} bigger plan available',
  'current.bigger_plans_other': '{count} bigger plans available',

  'fields.trial_status': 'Trial status',
  'fields.trial_started': 'Trial started',
  'fields.trial_ends': 'Trial ends',
  'fields.first_payment': 'First payment',
  'fields.first_payment_date': 'First payment date',
  'fields.next_billing': 'Next billing',
  'fields.billing_cycle': 'Billing cycle',
  'fields.every_cycle': 'Every {cycle}',
  'fields.auto_renew': 'Auto-renew',
  'fields.on': 'On',
  'fields.off': 'Off',
  'fields.off_ends': 'Off — ends {date}',
  'fields.off_manual': 'Off — you choose when to renew',
  'fields.payment_status': 'Payment status',
  'fields.nothing_charged_trial': 'Nothing charged during the trial',
  'fields.free_nothing_to_bill': 'Free — nothing to bill',
  'fields.paid_until': 'Paid until',
  'fields.renews': 'Renews',
  'fields.not_yet_paid': 'Not yet paid',
  'fields.never_expires': 'Never expires',
  'fields.failed_retrying': 'Failed — retrying until {date}',
  'fields.not_yet_authorised': 'Not yet authorised',
  'fields.paid_cycles_one': 'Paid · {count} cycle',
  'fields.paid_cycles_other': 'Paid · {count} cycles',
  'fields.awaiting_first_charge': 'Awaiting first charge',
  'fields.paid': 'Paid',
  'fields.nothing_to_pay': 'Nothing to pay',
  'fields.trial': 'Trial',
  'fields.trial_days_on_signup': '{days} days on new sign-up',
  'fields.current_period': 'Current period',

  'usage.title': 'Usage',
  'usage.description': 'Monthly counts reset on the 1st.',
  'usage.loading': 'Loading usage…',
  'usage.this_month': 'this month',
  'usage.no_limit': 'No limit on your plan.',

  'included.title': "What's included in {plan}",
  'included.your_plan': 'your plan',
  'included.description': 'Everything your plan covers, and what it would take to get the rest.',
  'included.count': '{included} of {total} included',
  'included.on_plan': 'On {plan}',
  'included.loading': 'Loading features…',

  'plans.title': 'Compare plans',
  'plans.description':
    'Your plan is highlighted. Upgrades take effect once the payment clears; moving to a cheaper plan applies immediately.',
  'plans.description_mandate':
    'Your plan is highlighted. Upgrades apply to your mandate straight away; moving to a cheaper plan waits for the period you have paid for to end.',
  'plans.auto_renews': 'auto-renews',
  'plans.gained_one': '+{count} feature:',
  'plans.gained_other': '+{count} features:',
  'plans.same_features': 'Same features as your plan.',
  'plans.upgrade': 'Upgrade',
  'plans.downgrade': 'Downgrade',
  'plans.loading': 'Loading plans…',
  'plans.downgrade_losses': 'What you would lose by moving to a cheaper plan',
  'plans.downgrade_loses_nothing': 'nothing — same features as your plan',

  'history.title': 'Billing history',
  'history.description':
    'What NeonCarts has charged you for your plan — not the invoices you issue to your shoppers. Automatic renewals are listed separately from the set-up of auto-renewal.',
  'history.in_progress_title': 'You have a payment in progress.',
  'history.in_progress_description': 'Until it is paid or cancelled, you cannot switch to a different plan.',
  'history.cancel_it': 'Cancel it',
  'history.cancel_failed': 'Could not cancel that payment.',
  'history.empty': 'Nothing yet — the Free plan is not charged for.',
  'history.no_match': 'No charges in this state.',
  'history.load_failed': 'Could not load your billing history.',
  'history.charges_one': '{count} charge',
  'history.charges_other': '{count} charges',
  'history.filter.label': 'Show charges',
  'history.filter.all': 'All',
  'history.receipt.download': 'Download receipt',
  'history.receipt.downloading': 'Downloading…',
  'history.receipt.failed': 'Could not download that receipt.',
  'history.summary.paid_total': 'Total paid',
  'history.summary.payments_one': 'Across {count} payment',
  'history.summary.payments_other': 'Across {count} payments',
  'history.summary.last_payment': 'Last payment',
  'history.summary.no_payments': 'No payments yet',
  'history.summary.failed': 'Failed',
  'history.summary.failed_hint': 'Declined or bounced charges',
  'history.summary.pending': 'Awaiting payment',
  'history.summary.pending_hint': 'Started but not yet paid',

  'footer.mandate':
    'Your plan renews automatically through Razorpay every {cycle}. You can cancel at any time and keep your paid features until the end of the period you have paid for; nothing is deleted when a plan ends.',
  'footer.default':
    'Plans marked auto-renew set up a Razorpay mandate and renew themselves; other paid plans are charged for one period at a time and you are asked again when it ends. Your entitlements change once the payment clears. Moving down to Free applies immediately.',

  'cancel.title': 'Cancel auto-renewal?',
  'cancel.title_trial': 'Cancel your free trial?',
  'cancel.keep': 'Keep my plan',
  'cancel.cancel_trial': 'Cancel trial',
  'cancel.cancel_subscription': 'Cancel subscription',
  'cancel.body_trial':
    'The Razorpay subscription is cancelled straight away, so the first payment of {amount} due on {due} is never taken. You keep {plan} until your trial ends on {date}, then move to the Free plan.',
  'cancel.body':
    'You keep {plan} until {date}. After that your account moves to the Free plan and Razorpay makes no further charges.',
  'cancel.nothing_deleted':
    'Nothing is deleted — your products, orders and customers stay exactly where they are, and you can subscribe again at any time.',
  'cancel.failed': 'Could not cancel the subscription.',

  // Subscribe screen
  'subscribe.loading': 'Loading your plan',
  'subscribe.load_failed': 'Could not load the plan. Refresh to try again.',
  'subscribe.free': 'Free',
  'subscribe.days_free': '{days} days free',
  'subscribe.off_first_payment': '{amount} off your first payment',
  'offer.with_code': '{amount} off with',
  'subscribe.your_plan': 'Your plan',
  'subscribe.already_subscribed':
    'You already have a subscription on {plan}. Change it from {link} rather than starting another.',
  'subscribe.already_auto_renewing':
    'You already have an auto-renewing subscription on {plan}. Change or cancel it from {link} rather than starting another.',
  'subscribe.billing_page_link': 'your billing page',
  'subscribe.no_longer_on_sale': 'The plan {code} is no longer on sale. Choose another below.',
  'subscribe.error_with_link': '{error} {link}.',
  'subscribe.setup_failed': 'Could not set up the subscription.',
  'subscribe.manage_plan': 'Manage your plan',
  'subscribe.already_on': 'You are already on {plan}. {link}.',
  'subscribe.already_on_trial': 'You are already on {plan} — your free trial runs until {date}. {link}.',
  'subscribe.dashboard_link': 'Go to your dashboard',
  'subscribe.continue_to_razorpay': 'Continue to Razorpay',
  'subscribe.start_trial': 'Pay later - start {days}-day free trial',
  'subscribe.pay_and_autorenew': 'Pay {amount} today & set up auto-renewal',
  'subscribe.consent_discounted':
    'You pay {amount} today, including your first {cycle} at the discounted price. By continuing you authorise Razorpay to charge {price} every {cycle} from {date} until you cancel. Cancel anytime from your billing page.',
  'subscribe.subscribe_for': 'Subscribe for {amount}',
  'subscribe.continue_on': 'Continue on {plan}',
  'subscribe.opening_razorpay': 'Opening Razorpay…',
  'subscribe.owner_only': 'Only an owner or admin of this business can set up its plan.',
  'subscribe.choose_different': 'Choose a different plan',
  'subscribe.secure_checkout':
    "Card, UPI and netbanking details are entered on Razorpay's secure checkout, never on this page.",
  'subscribe.hero.start_free': 'Start free,',
  'subscribe.hero.then_price': 'then {price} / {cycle}',
  'subscribe.hero.was_now': 'was {price}, now {payable} / {cycle}',
  'subscribe.schedule.today': 'Today',
  'subscribe.schedule.always': 'Always',
  'subscribe.schedule.cancel_anytime': 'Cancel anytime',
  'subscribe.schedule.free_no_card': 'Free — no card needed',
  'subscribe.schedule.discounted': '{amount} ({discount} off)',
  'subscribe.schedule.renews_at': 'Renews at {price}',
  'subscribe.schedule.renews': 'Renews',
  'subscribe.renewal.free': 'Nothing to pay and no card to enter. Move to a paid plan whenever you need more.',
  'subscribe.renewal.trial':
    'Your first payment of {price} is taken on {date}, then {price} every {cycle} (next on {next}). Cancel before {date} and you are not charged.',
  'subscribe.renewal.discounted':
    '{amount} today with your referral discount, then {price} every {cycle}. Next payment {next}.',
  'subscribe.renewal.paid': '{price} today, then {price} every {cycle}. Next payment {next}.',

  'referral.badge': 'Referral discount',
  'referral.referred_by': '{partner} referred you.',
  'referral.first_payment_only': 'Applies to your first payment; auto-renewal is at {price}.',
  'referral.applies_to': 'Your referral discount applies to {plans}.',
  'referral.still_applies': 'It still applies to {plans}.',
  'referral.not_covered': 'Your referral discount does not apply to {plan}.',
  'referral.below_minimum': 'Your referral discount applies to larger payments than {plan}.',
  'referral.consumed': 'You have already used your referral discount.',
  'referral.expired': 'Your referral discount has expired.',
  'referral.trial_plan':
    '{plan} starts with a {days}-day free trial, so there is no first payment to discount. Your referral discount is still waiting for a later one.',
  'referral.plan_list_single': 'the {plan} plan',
  'referral.plan_list_multiple': 'the {plans} and {last} plans',

  'trade.trial_detail': 'Pay nothing today. {price} is taken when the trial ends, then every {cycle}.',
  'trade.discount_detail':
    'Start now for {amount} instead of {price}, then {price} every {cycle}. No free trial.',

  // The free-trial checkbox at checkout
  'trial.legend': 'Free trial or discount',
  'trial.skip_detail': 'Your plan starts today: pay {price} now, then every {cycle}.',
  'trial.untick_for_discount':
    'Untick to take {amount} off your first payment instead. A discount cannot be combined with the free trial.',

  // A promo or partner code typed at checkout
  'promo.toggle': 'Have a promo or partner code?',
  'promo.label': 'Promo or partner code',
  'promo.hint': 'From a partner, influencer or offer. Once applied, a code stays on your account.',
  'promo.apply': 'Apply',
  'promo.applying': 'Applying…',
  'promo.applied': 'Code {code} applied',
  'promo.locked': 'Codes stay on your account once applied, so the partner who sent you keeps the credit.',
  'promo.failed': 'Could not apply that code. Try again.',

  // Plan chooser
  'chooser.title': 'Choose your plan',
  'chooser.description': 'Pick the one that fits how you are selling today. You can change it whenever you like.',
  'chooser.trial_badge': '{days}-day trial',
  'chooser.choose': 'Choose {plan}',
  // Under the per-month figure on a card: the charge the per-month price is
  // worked out from, which is the amount that actually leaves the account.
  'chooser.billed': '{amount} billed {cycle}',

  // Optional one-time store setup add-on
  'setup_addon.legend': 'Store setup by NeonCarts team',
  'setup_addon.checkbox': 'Add store setup by NeonCarts team – {amount}',
  'setup_addon.scope': 'Up to {products} products, {images} images each, within your plan limit. One-time, with this payment only.',
  'setup_addon.plan_limit_note': 'Your plan allows {limit} products, so we\'ll set up {limit}. Upgrade later to add more.',
  'setup_addon.total_today': 'Total today',
  'setup_addon.total_line': '{plan} {plan_amount} + setup {fee} = {total}',
  'setup_addon.trial_note': 'The setup fee is charged now, when you authorise; the plan is first charged when your trial ends.',
  'setup_addon.pay_for': 'Pay {amount}',
  'setup_addon.purchased_title': 'Store setup purchased',
  'setup_addon.purchased_status': 'Status: {status}',
  'setup_addon.purchased_scope': 'Up to {products} products, {images} images each, within your plan limit.',
  'setup_addon.view_request': 'View setup request',
  'setup_addon.status.REQUESTED': 'Requested',
  'setup_addon.status.ASSIGNED': 'Requested',
  'setup_addon.status.IN_PROGRESS': 'In progress',
  'setup_addon.status.COMPLETED': 'Completed',
  'setup_addon.status.CANCELLED': 'Cancelled',
  'setup_addon.status.DECLINED': 'Declined',
  'setup_addon.chooser_line': 'Optional store setup: {amount} one-time, up to {products} products with {images} images each.',
  'setup_addon.later_title': 'Let our team set up your store',
  'setup_addon.later_description': 'A NeonCarts team member sets up up to {products} products with {images} images each, within your plan limit. Includes {hours} hours of team time. One-time payment.',
  'setup_addon.later_buy': 'Buy store setup – {amount}',
  'setup_addon.later_paid': 'Payment received. Our team will pick up your store setup shortly.',
  'setup_addon.later_badge': 'One-time offer for your store',
  'setup_addon.later_pitch': 'Skip the busywork. A NeonCarts team member logs in and sets up your products, photos and store basics for you, so you can start selling sooner.',
  'setup_addon.later_chip_products': 'Up to {count} products',
  'setup_addon.later_chip_images': '{count} images per product',
  'setup_addon.later_chip_hours': '{count} hours of team time',
  'setup_addon.later_once': 'One-time payment, never renewed',
  'setup_addon.later_hide': 'Hide this offer for a week',
  'setup_addon.later_snooze': 'Maybe later',
  'setup_addon.invoice_line': 'incl. {amount} store setup',

  // GST on plan purchases: the payment summary and the invoice history
  'gst.plus_gst': '{price} + GST',
  'gst.plus_gst_short': '+ GST',
  'gst.summary_title': 'Payment summary',
  'gst.plan_price': 'Plan price',
  'gst.plan_today_trial': 'Plan (charged when the trial ends)',
  'gst.discount': 'Referral discount',
  'gst.setup': 'Store setup (one-time)',
  'gst.tax_line': 'GST ({rate}%)',
  'gst.split_intra': 'CGST {half}% {cgst} + SGST {half}% {sgst}',
  'gst.split_inter': 'IGST {rate}% {igst}',
  'gst.place_of_supply': 'Place of supply: {state}',
  'gst.total_payable': 'Total payable',
  'gst.renews_at': 'Each renewal after this: {amount}, GST included.',
  'gst.calculating': 'Calculating GST…',
  'gst.quote_failed': 'Could not work out the GST for this plan just now. Razorpay shows the exact amount before you pay.',
  'gst.your_gstin': 'GST invoice to GSTIN {gstin}',
  'gst.no_gstin': 'No GSTIN on your business. Add one to claim this GST as input tax credit.',
  'gst.add_details': 'Add GSTIN',
  'gst.edit_details': 'Edit GST details',
  'gst.gstin_label': 'Your GSTIN',
  'gst.gstin_hint': 'Leave empty if your business is not GST-registered.',
  'gst.state_label': 'Billing state',
  'gst.save': 'Save',
  'gst.saving': 'Saving…',
  'gst.cancel': 'Cancel',
  'gst.save_failed': 'Could not save your GST details. Try again.',
  'invoices.gst_line': 'incl. {amount} GST ({rate}%)',
  'invoices.number': 'Invoice {number}',
  'history.receipt.download_invoice': 'Download invoice',

  // Mandate verification, refunds, scheduled set-ups and credits in the history
  'invoices.kind.MANDATE_VERIFICATION': 'Mandate verification',
  'invoices.status.REFUNDED': 'Refunded',
  'invoices.refunded_on': 'Refunded {date}',
  'invoices.scheduled_due': 'Scheduled · due {date}',
  'invoices.mandate_verification.refunded': 'Refunded by Razorpay',
  'invoices.mandate_verification.refund_pending': 'Refund on its way from Razorpay',
  'invoices.mandate_verification.refund_hint': 'Usually within 5–7 working days.',
  'invoices.credit_line': 'Credit applied: −{amount} for {days} of {period_days} days',
  'history.credit_note.download': 'Download credit note',
  'history.credit_note.downloading': 'Downloading…',
  'history.credit_note.failed': 'Could not download that credit note.',
  'history.summary.scheduled': 'Scheduled',
  'history.summary.scheduled_hint': 'Due when the current period ends',

  // The quote shown before a paid plan change
  'quote.title': 'Confirm your plan change',
  'quote.title_renewal': 'Renew {plan}',
  'quote.list_price': '{plan} list price',
  'quote.credit_line': 'Credit for {days} of {period_days} unused days of {plan}',
  'quote.period': 'Your {plan} period runs {period_start} → {period_end}',
  'quote.next_period': 'Your next period runs {period_start} → {period_end}',
  'quote.deferred': 'Changes on {date} — nothing to pay today',
  'quote.deferred_reason.FEATURE_DOWNGRADE':
    'The new plan includes less than your current one, so it starts when the period you have paid for ends.',
  'quote.deferred_reason.SHORTER_CYCLE':
    'The new plan bills on a shorter cycle, so it starts when the period you have paid for ends.',
  'quote.deferred_reason.CREDIT_EXCEEDS_PRICE':
    'Your unused credit is worth more than the new plan costs, so the change waits for your current period to end.',
  'quote.deferred_reason.PREPAID_PERIOD_AHEAD':
    'You have already paid for your next period, so the change starts when that period ends.',
  'quote.loading': 'Working out your price…',
  'quote.failed':
    'Could not work out the exact price just now. Razorpay shows the amount before you pay.',
  'quote.cancel': 'Go back',
  'quote.confirm_pay': 'Continue to payment',
  'quote.confirm_change': 'Confirm change',
  'quote.confirming': 'Working…',

  'notices.payment_confirmed': 'Payment confirmed. You are now on {plan}.',
  'notices.mandate_verification_charge':
    'Your bank will show a ₹5 charge from Razorpay that verifies the mandate; Razorpay refunds it automatically.',

  // A hand-paid plan past its period end, and the lock after the grace period
  'grace.notice': 'Your plan ended on {date}; pay by {ends_at} to keep your store open.',
  'grace.renew': 'Renew now',
  'lock.title': 'Your store is closed to shoppers',
  'lock.reason.PERIOD_ENDED': 'Your {plan} plan ended on {date} and the grace period ran out on {grace}.',
  'lock.reason.GRACE_EXPIRED':
    'Razorpay could not collect your {plan} renewal and the grace period ran out on {grace}.',
  'lock.reason.TRIAL_ENDED': 'Your {plan} trial ended on {date}.',
  'lock.reason.default': 'Your {plan} plan lapsed on {date}.',
  'lock.consequence':
    'Your store is closed to shoppers and your dashboard is limited to billing until a plan is paid for.',
  'lock.amount_due': 'Renewing {plan} costs {amount}, GST included.',
  'lock.renew': 'Renew {plan}',
  'lock.choose_other': 'Or choose another plan below.',
  'lock.ask_owner': 'Ask the owner of this business to renew the plan.',
  'lock.billing_link': 'Renew your plan',

  // A mandate change waiting for Checkout, and a failed mandate's way out
  'pending_change.title': 'Finish paying for {plan}',
  'pending_change.description':
    'Your move to {plan} is waiting for Razorpay Checkout. {amount} is due; the offer expires on {date}.',
  'pending_change.finish': 'Finish paying',
  'pending_change.cancel': 'Cancel the change',
  'past_due.update_payment': 'Update payment method',
  'domains.title': 'Domain purchases',
  'domains.description':
    'Domains you bought or renewed through NeonCarts. Each paid one has its own receipt.',
  'domains.manage': 'Manage domains',
  'domains.kind.REGISTRATION': 'Registration',
  'domains.kind.RENEWAL': 'Renewal',
  'domains.years_one': '{count} year',
  'domains.years_other': '{count} years',
  'domains.count_one': '{count} domain payment',
  'domains.count_other': '{count} domain payments',
  'domains.receipt_number': 'Receipt {number}',
  'domains.receipt_failed': 'Could not download that domain receipt.',
  // The free dashboard trial before a plan is chosen
  'panel_trial.start': 'Skip Payment - Start trial',
  'panel_trial.starting': 'Starting your trial…',
  'panel_trial.start_failed': 'Your free trial could not be started. Please try again.',
  'panel_trial.started': 'Your {days}-day free trial has started. Choose a plan any time before it ends.',
  'panel_trial.running': 'You are on a free trial with {days} days left. Choose a plan below, or {link}.',
  'panel_trial.running_last_day': 'Your free trial ends within a day. Choose a plan below, or {link}.',
  'panel_trial.back_to_dashboard': 'go back to your dashboard',
  'panel_trial.ended': 'Your free trial has ended. Choose a paid plan to keep using your dashboard and reopen your store.',
  'panel_trial.badge_label': 'Free trial',
  'panel_trial.badge_days': '{days} days left',
  'panel_trial.badge_last_day': 'Last day',
  'panel_trial.choose_plan': 'Choose a plan',
  'lock.reason.PANEL_TRIAL_OVER': 'Your free trial ended on {date}.',
};

export default billing;
